// View analysis job details
import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import analysisJobApi from '../services/analysisJobApi';
import JobProgress from '../components/JobProgress';
import FileCard from '../components/FileCard';
import Loading from '../components/Loading';

const AnalysisJobDetails = () => {
  const { jobId } = useParams();
  const [analysisJob, setAnalysisJob] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('results');

  // Fetch analysis job data on component mount
  useEffect(() => {
    const fetchAnalysisJob = async () => {
      try {
        setIsLoading(true);
        const data = await analysisJobApi.getAnalysisJob(jobId);
        setAnalysisJob(data);
      } catch (err) {
        setError(err.message);
        console.error('Error fetching analysis job:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAnalysisJob();
    
    // Poll for updates if job is in progress
    const intervalId = setInterval(async () => {
      try {
        const data = await analysisJobApi.getAnalysisJob(jobId);
        setAnalysisJob(data);
        
        // Stop polling if job is completed or failed
        if (['completed', 'failed', 'cancelled'].includes(data.status)) {
          clearInterval(intervalId);
        }
      } catch (err) {
        console.error('Error polling analysis job:', err);
        clearInterval(intervalId);
      }
    }, 5000); // Poll every 5 seconds
    
    // Clean up interval on unmount
    return () => clearInterval(intervalId);
  }, [jobId]);

  const handleDeleteJob = async () => {
    // if (!window.confirm("Are you sure you want to delete this analysis job?")) {
    //   return;
    // }
    
    try {
      await analysisJobApi.deleteAnalysisJob(jobId);
      // Redirect to the jobs list
      window.location.href = '/analysis-jobs';
    } catch (err) {
      setError(err.message);
      console.error('Error deleting analysis job:', err);
    }
  };

  // Format the date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };
  
  // Format cost
  const formatCost = (cost) => {
    if (!cost) return 'N/A';
    return `$${parseFloat(cost).toFixed(4)}`;
  };

  if (isLoading) {
    return <Loading message="Loading analysis job details..." />;
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-500 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-red-700">Error: {error}</p>
            <div className="mt-2">
              <Link to="/advanced/analysis-jobs" className="text-sm text-red-700 underline">Back to Analysis Jobs</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!analysisJob) {
    return (
      <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M8.485 3.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 3.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-yellow-700">Analysis job not found.</p>
            <div className="mt-2">
              <Link to="/advanced/analysis-jobs" className="text-sm text-yellow-700 underline">Back to Analysis Jobs</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Calculate total files from multiple sources
  const getTotalFileCount = () => {
    const directFiles = analysisJob.files?.length || 0;
    const scrapeJobFiles = analysisJob.scrapeJobPartsDetails?.reduce((acc, part) => acc + (part.fileCount || 0), 0) || 0;
    return directFiles + scrapeJobFiles;
  };

  return (
    <div>
      <div className="md:flex md:items-center md:justify-between mb-6">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold text-gray-900">Analysis Job</h1>
          <div className="mt-1 flex flex-col sm:flex-row sm:flex-wrap sm:mt-0 sm:space-x-6">
            <div className="mt-2 flex items-center text-sm text-gray-500">
              <svg className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
              </svg>
              Created: {formatDate(analysisJob.createdAt)}
            </div>
            <div className="mt-2 flex items-center text-sm text-gray-500">
              <svg className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
              Files: {getTotalFileCount()}
            </div>
            <div className="mt-2 flex items-center text-sm text-gray-500">
              <svg className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
              </svg>
              Model: {analysisJob.modelName || 'Unknown'}
            </div>
            {analysisJob.completedAt && (
              <div className="mt-2 flex items-center text-sm text-gray-500">
                <svg className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
                Completed: {formatDate(analysisJob.completedAt)}
              </div>
            )}
          </div>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          <Link to="/advanced/analysis-jobs" className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
            Back to Analysis Jobs
          </Link>
          <button
            onClick={handleDeleteJob}
            className="ml-3 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Delete Job
          </button>
        </div>
      </div>

      {/* Job status and progress */}
      <JobProgress 
        jobId={jobId} 
        jobType="analysisJob" 
        progressItems={analysisJob.progressItems || []} 
        isCompleted={['completed', 'failed', 'cancelled'].includes(analysisJob.status)}
      />

      {/* Analysis metrics (tokens, cost) */}
      {(analysisJob.inputTokens || analysisJob.outputTokens) && (
        <div className="bg-white shadow rounded-lg p-4 mt-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Analysis Metrics</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gray-50 rounded-md p-4">
              <p className="text-sm text-gray-500">Input Tokens</p>
              <p className="text-xl font-semibold text-gray-900">{analysisJob.inputTokens?.toLocaleString() || 'N/A'}</p>
            </div>
            <div className="bg-gray-50 rounded-md p-4">
              <p className="text-sm text-gray-500">Output Tokens</p>
              <p className="text-xl font-semibold text-gray-900">{analysisJob.outputTokens?.toLocaleString() || 'N/A'}</p>
            </div>
            <div className="bg-gray-50 rounded-md p-4">
              <p className="text-sm text-gray-500">Estimated Cost</p>
              <p className="text-xl font-semibold text-gray-900">{formatCost(analysisJob.totalEstimatedCost)}</p>
            </div>
          </div>
        </div>
      )}

      {/* Tabs for Results/Input Files */}
      <div className="mt-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('results')}
              className={`${
                activeTab === 'results'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Analysis Results
            </button>
            <button
              onClick={() => setActiveTab('files')}
              className={`${
                activeTab === 'files'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Input Files
            </button>
            <button
              onClick={() => setActiveTab('prompt')}
              className={`${
                activeTab === 'prompt'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              Analysis Prompt
            </button>
          </nav>
        </div>

        {/* Results Tab */}
        {activeTab === 'results' && (
          <div className="bg-white shadow rounded-lg p-4 mt-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Analysis Results</h3>
            
            {analysisJob.status !== 'completed' ? (
              <div className="p-6 text-center">
                {analysisJob.status === 'failed' ? (
                  <div className="text-red-500">
                    <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    <h4 className="mt-2 text-lg font-medium">Analysis Failed</h4>
                    <p className="mt-1">Please check the progress log for details.</p>
                  </div>
                ) : analysisJob.status === 'cancelled' ? (
                  <div className="text-orange-500">
                    <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                    <h4 className="mt-2 text-lg font-medium">Analysis Cancelled</h4>
                    <p className="mt-1">This analysis job was cancelled.</p>
                  </div>
                ) : (
                  <div>
                    {analysisJob.resultText ? (
                      <div className="mb-6">
                        <div className="text-blue-500 flex items-center justify-center mb-4">
                          <svg className="animate-spin h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                          </svg>
                          <span className="font-medium">Analysis in Progress - Showing Partial Results</span>
                        </div>
                        <div className="prose max-w-none">
                          <div className="bg-gray-50 p-4 rounded-md text-sm text-gray-800 overflow-auto border-l-4 border-blue-400">
                            <ReactMarkdown>
                              {analysisJob.resultText}
                            </ReactMarkdown>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-blue-500">
                        <svg className="animate-spin mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                        </svg>
                        {/* <h4 className="mt-2 text-lg font-medium">Analysis in Progress</h4> */}
                        {/* <p className="mt-1">Waiting for initial results to appear...</p> */}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : !analysisJob.resultText ? (
              <div className="p-6 text-center text-yellow-500">
                <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                </svg>
                <h4 className="mt-2 text-lg font-medium">No Results Available</h4>
                <p className="mt-1">The analysis completed but no results were generated.</p>
              </div>
            ) : (
              <div className="prose max-w-none">
                <div className="bg-gray-50 p-4 rounded-md text-sm text-gray-800 overflow-auto">
                  <ReactMarkdown>
                    {analysisJob.resultText}
                  </ReactMarkdown>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Files Tab */}
        {activeTab === 'files' && (
          <div className="mt-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Input Files</h3>
            
            {/* Direct Files */}
            {analysisJob.fileDetails && analysisJob.fileDetails.length > 0 && (
              <div className="mb-6">
                <h4 className="text-md font-medium text-gray-700 mb-2">Individual Files</h4>
                <div className="space-y-4">
                  {analysisJob.fileDetails.map(file => (
                    <FileCard key={file.fileId} file={file} />
                  ))}
                </div>
              </div>
            )}
            
            {/* Scrape Job Parts */}
            {analysisJob.scrapeJobPartsDetails && analysisJob.scrapeJobPartsDetails.length > 0 && (
              <div>
                <h4 className="text-md font-medium text-gray-700 mb-2">Scrape Job Content</h4>
                <div className="bg-white shadow overflow-hidden sm:rounded-md">
                  <ul className="divide-y divide-gray-200">
                    {analysisJob.scrapeJobPartsDetails.map((part, index) => (
                      <li key={index}>
                        <Link to={`/advanced/scrape-jobs/${part.scrapeJobId}`} className="block hover:bg-gray-50 p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-blue-600">
                                Scrape Job: {part.scrapeJobId}
                              </p>
                              <p className="mt-1 text-sm text-gray-500">
                                Content Type: <span className="font-medium">{part.fileType}</span>
                              </p>
                            </div>
                            <div className="ml-4">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                {part.fileCount} files
                              </span>
                            </div>
                          </div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
            
            {/* No files case */}
            {(!analysisJob.fileDetails || analysisJob.fileDetails.length === 0) && 
             (!analysisJob.scrapeJobPartsDetails || analysisJob.scrapeJobPartsDetails.length === 0) && (
              <div className="bg-white shadow rounded-lg p-6 text-center">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"></path>
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No files</h3>
                <p className="mt-1 text-sm text-gray-500">
                  No input files found for this analysis job.
                </p>
              </div>
            )}
          </div>
        )}
        
        {/* Prompt Tab */}
        {activeTab === 'prompt' && (
          <div className="bg-white shadow rounded-lg p-4 mt-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Analysis Prompt</h3>
            
            <div className="mb-6">
              <h4 className="text-md font-medium text-gray-700 mb-2">Parameters</h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 rounded-md p-4">
                  <p className="text-sm text-gray-500">Model</p>
                  <p className="font-medium text-gray-900">{analysisJob.modelName || 'N/A'}</p>
                </div>
                <div className="bg-gray-50 rounded-md p-4">
                  <p className="text-sm text-gray-500">Temperature</p>
                  <p className="font-medium text-gray-900">{analysisJob.temperature || 'N/A'}</p>
                </div>
                <div className="bg-gray-50 rounded-md p-4">
                  <p className="text-sm text-gray-500">Top P</p>
                  <p className="font-medium text-gray-900">{analysisJob.topP || 'N/A'}</p>
                </div>
                <div className="bg-gray-50 rounded-md p-4">
                  <p className="text-sm text-gray-500">Max Output Tokens</p>
                  <p className="font-medium text-gray-900">{analysisJob.maxOutputTokens || 'N/A'}</p>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="text-md font-medium text-gray-700 mb-2">Prompt Text</h4>
              {analysisJob.prompt ? (
                <div className="bg-gray-50 rounded-md p-4">
                  <pre className="whitespace-pre-wrap text-sm text-gray-800">{analysisJob.prompt}</pre>
                </div>
              ) : (
                <p className="text-gray-500 italic">No prompt text available.</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalysisJobDetails;